<template>
  <div class="device_function_box">
    <div class="device_function_stack">
      <section v-if="regularDeviceFunctions.length > 0" class="device_function_section">
        <div class="device_function_grid">
          <button v-for="item in regularDeviceFunctions" :key="item.id" type="button" class="device_function_item" :class="{ selected: item.id === actionSet.deviceFunctionValue.value }" @click="select(item.id)">
            <div class="icon_wrapper" v-html="getDeviceFunctionIconSvg(item.id)" />
            <div class="media_label">{{ $t(item.labelKey) }}</div>
            <div v-if="getOnboardSlotLabel(item.id)" class="device_function_slot_badge">{{ getOnboardSlotLabel(item.id) }}</div>
          </button>
        </div>
      </section>

      <div v-else class="device_function_empty">
        <div class="empty_title">{{ $t('action_set_device_function') }}</div>
        <div class="empty_text">{{ $t('device_function_rules_pending') }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { actionSet } from '@/service/driver/action_set'
import { DeviceCapability } from '@/service/device_capability'
import { configSet } from '@/service/driver/config_set'
import { InternalActionKeyId } from '@/define/internal_action_keys'
import { type VK } from '@/define/define'
import i18n from '@/languages/i18n'
import { getDeviceFunctionIconSvg } from './device_function_icons'

const { t } = i18n.global
const onboardSwitchActionIds = [InternalActionKeyId.deviceSwitchToProfile1, InternalActionKeyId.deviceSwitchToProfile2, InternalActionKeyId.deviceSwitchToProfile3, InternalActionKeyId.deviceSwitchToProfile4] as VK[]
const regularDeviceFunctions = computed(() => DeviceCapability.getActionSettingDeviceFunctionActionKeys())

const getOnboardSlotLabel = (id: number): string | null => {
  const slotIndex = onboardSwitchActionIds.indexOf(id as VK)
  if (slotIndex < 0) return null
  if (!configSet.isOnboardConfigSupported) return null
  const slot = configSet.onboardConfigList.value.find((s) => s.Index === slotIndex)
  const localIdx = slot?.LocalConfigIndex ?? null
  if (localIdx !== null) {
    const configName = configSet.configList.value[localIdx]?.Name || ''
    return `${t('config')} ${slotIndex + 1}: ${configName}`
  }
  return `${t('config')} ${slotIndex + 1}: ${t('keyboard_preset_slot_empty')}`
}

const select = (id: number) => {
  actionSet.deviceFunctionValue.value = id
}
</script>

<style lang="less" scoped>
.device_function_box {
  width: 100%;
  padding: var(--spacing-inner-padding);
}

.device_function_stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.device_function_section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty_text {
  color: var(--color-text-hint);
  font-size: 12px;
  line-height: 1.5;
}

.empty_title {
  color: var(--color-dark);
  font-size: 15px;
  font-weight: 700;
}

.device_function_grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  grid-auto-rows: 112px;
  gap: var(--spacing-grid-gap);
}

.device_function_item {
  border: none;
  border-radius: var(--radius-s);
  background: var(--bg-card, rgba(255, 255, 255, 0.9));
  height: 100%;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  text-align: center;
  outline: none;
  .mgx_card_item_effect();
}

.icon_wrapper {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-s);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-gray-1);
  color: var(--color-dark);
  transition: var(--transition-base);

  :deep(svg) {
    width: 18px;
    height: 18px;
    opacity: 0.8;
    transition: var(--transition-base);
  }
}

.media_label {
  text-align: center;
  font-size: 11px;
  line-height: 1.2;
  color: var(--color-text-hint);
  font-weight: 500;
  transition: var(--transition-base);
  width: 100%;
  min-height: 1.2em;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device_function_slot_badge {
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  color: var(--brand-color-pure);
  line-height: 1.2;
  padding: 2px 6px;
  border-radius: var(--radius-pill);
  background: rgba(var(--color-brand-1-rgb), 0.08);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device_function_empty {
  min-height: 220px;
  border-radius: var(--radius-m);
  background: var(--color-white);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}
</style>
